import { Component, OnInit, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { AccountView } from '@api-proxies';
import { AppEditorFactory } from '@factories/app-editor-factory/app-editor-factory';
import { EditorType } from '@factories/app-editor-factory/enum/editor-type';
import { AccountService } from '@http/clients/account.service';
import { Account } from '@models/Account';
import { AwaiterService } from '@services/awaiter.service';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import {
    EditorItemComponent,
    IconSanitizerService,
    IEditorItemConfig,
    ModalComponentBase,
    SnackbarNotificationService,
    ValidatorType
} from 'ngx-lib';
import { LoadingButtonComponent } from '../../loading-button/loading-button.component';

@Component({
    selector: 'app-modal-add-account',
    templateUrl: './modal-add-account.component.html',
    styleUrls: ['./modal-add-account.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [...CDK_IMPORTS, EditorItemComponent, LoadingButtonComponent]
})
export class ModalAddAccountComponent extends ModalComponentBase<undefined, AccountView> implements OnInit {
    factory: AppEditorFactory;

    configItems: IEditorItemConfig[];

    account: Account;

    isReadOnly: boolean;

    @ViewChildren('editorItem')
    editors?: QueryList<EditorItemComponent>;

    isLoading: boolean;

    closeIcon: SafeHtml;

    constructor(
        private readonly accountService: AccountService,
        private readonly snackbarNotificationService: SnackbarNotificationService,
        private readonly awaiter: AwaiterService,
        private readonly iconSanitizer: IconSanitizerService
    ) {
        super();
        this.factory = new AppEditorFactory();
        this.account = new Account();
        this.isReadOnly = false;
        this.configItems = [];
        this.isLoading = false;
        this.closeIcon = this.iconSanitizer.getIcon('closeModal');
    }

    ngOnInit(): void {
        this.configItems = [
            {
                label: 'Name',
                componentId: EditorType.textInput,
                fieldName: 'name',
                bindContext: this.account,
                isReadOnly: this.isReadOnly,
                validations: [
                    { type: ValidatorType.required },
                    { type: ValidatorType.string, args: { maxLength: 100 } }
                ],
                additionalInfo: {
                    shouldTrimSpaces: true
                }
            },
            {
                label: 'Website',
                componentId: EditorType.link,
                fieldName: 'webSite',
                bindContext: this.account,
                isReadOnly: this.isReadOnly,
                validations: [{ type: ValidatorType.string, args: { maxLength: 100 } }]
            },
            {
                label: 'Linkedin',
                componentId: EditorType.link,
                fieldName: 'linkedin',
                bindContext: this.account,
                isReadOnly: this.isReadOnly,
                validations: [{ type: ValidatorType.string, args: { maxLength: 100 } }]
            },
            {
                label: 'Type',
                componentId: EditorType.companyType,
                fieldName: 'accountTypeId',
                bindContext: this.account,
                isReadOnly: this.isReadOnly
            },
            {
                label: 'Markets',
                componentId: EditorType.entity,
                fieldName: 'accountMarkets',
                bindContext: this.account,
                entityId: 'accountMarket',
                isReadOnly: this.isReadOnly
            }
        ];
    }

    private validate(): boolean {
        return this.editors?.map((editorItem) => editorItem.validate()).every((editorItem) => editorItem) ?? false;
    }

    async saveAccount(): Promise<void> {
        if (!this.validate()) return;

        await this.awaiter.awaitAction(
            'Saving Account...',
            async () => {
                const response = await this.accountService.save(this.account);
                if (response) this.snackbarNotificationService.success('Account created successfully.');
                this.close(response);
            },
            (loading) => (this.isLoading = loading)
        );
    }
}
